import {
  FormControl,
  FormHelperText,
  TextField,
  TextFieldProps,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useField, useFormikContext } from "formik";
import { KeyboardEventHandler } from "react";

const useStyles = makeStyles({
  root: {
    "& .MuiOutlinedInput-root": {
      height: "48px",
    },
  },
});

function FormTextInput({
  name,
  fullWidth = true,
  label,
  type = "text",
  ...rest
}: TextFieldProps & { name: string }): JSX.Element {
  const classes = useStyles();
  const formikContext = useFormikContext();
  const { isSubmitting: disabled } = formikContext;
  const [field, meta, helper] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : "";
  const hasError = !!errorText;

  const isNumeric = type === "number" || rest.inputMode === "numeric";

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = function (e) {
    if (!isNumeric) return;
    if (
      !name.toLowerCase().includes("amount") &&
      ["e", "-", "."].includes(e.key)
    ) {
      e.preventDefault();
      return;
    }
    if (["e", "-"].includes(e.key)) e.preventDefault();
  };

  interface GetValidValue {
    (chars: string[], value: string): string;
  }

  const getValidValue: GetValidValue = function (chars, value) {
    const valueArray = String(value).split("");
    const validValue = valueArray.filter((v) => !chars.includes(v));
    if (valueArray.length !== validValue.length) {
      return "";
    }
    return value;
  };

  const onChange: TextFieldProps["onChange"] = function (e) {
    field.onChange(e);
    if (rest.onChange) {
      rest.onChange(e);
    }
    if (!isNumeric) return;
    const value = e.target.value;
    const isAmountField = name.toLowerCase().includes("amount");

    if (!isAmountField) {
      helper.setValue(getValidValue(["e", "-", "."], value));
      return;
    }

    helper.setValue(getValidValue(["e", "-"], value));
  };

  const onBlur: TextFieldProps["onBlur"] = function (e) {
    field.onBlur(e);
    if (rest.onBlur) {
      rest.onBlur(e);
    }
  };

  return (
    <FormControl fullWidth={fullWidth} error={hasError}>
      <Typography style={{ fontWeight: 600, fontFamily: "OpenSans-SemiBold" }}>
        {label}
      </Typography>
      <TextField
        className={classes.root}
        fullWidth={fullWidth}
        disabled={disabled}
        margin="dense"
        variant="outlined"
        onKeyDown={onKeyDown}
        type={type}
        {...field}
        {...rest}
        onBlur={onBlur}
        onChange={onChange}
        inputProps={{
          maxLength: 40,
        }}
        error={hasError}
      />
      <FormHelperText>{errorText}</FormHelperText>
    </FormControl>
  );
}

export default FormTextInput;
